import React, { useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import Box from 'ui-box';
import {
  Button,
  Container,
  Grid,
  majorScale,
  Menu,
  MenuDivider,
  MenuItem,
  minorScale,
  PageContainer,
  Popover,
  Section,
  Table,
  PageHeader,
  Spinner,
  Tag,
  useAlert,
  useToast,
} from '../../../npaper';
import { CreditCardDialog } from './CreditCardDialog';
import { IBilling, useStores } from '../../../stores';
import { UpgradeSideSheet } from './UpgradeSideSheet';
import { useEffect } from 'react';
import {
  FormattedDate,
  FormattedMessage,
  FormattedNumber,
  useIntl,
} from 'react-intl';

export const SubscriptionPage = () => {
  const history = useHistory();
  const location = useLocation();
  const intl = useIntl();
  const toaster = useToast();
  const [billingState, setBillingState] = useState(0);
  const [addNewCard, setAddNewCard] = useState(false);

  const [billing, setBilling] = useState<IBilling>();

  const { authStore, organisationStore } = useStores();
  const organisation = authStore.currentOrganisation!;

  const [upgrade, setUpgrade] = useState(false);

  const cancelAlert = useAlert({
    intent: 'danger',
    icon: 'warning-sign',
    title: intl.formatMessage({ defaultMessage: 'Confirm cancellation' }),
    message: intl.formatMessage(
      {
        defaultMessage:
          'You are about to cancel your paid subscription. You can access your account until {endDate, date}. After this date, your account will be closed and scheduled for deletion after 90 days.',
      },
      {
        endDate: organisation.subscription
          ? new Date(organisation.subscription!.current_period_end!)
          : new Date(),
      }
    ),
    confirmButtonText: intl.formatMessage({
      defaultMessage: 'Yes, cancel subscription',
    }),
  });

  const params = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );

  useEffect(() => {
    if (
      params.get('upgrade') &&
      (!organisation.subscription ||
        organisation.subscription.trialing ||
        organisation.subscription.cancel_at_period_end ||
        organisation.subscription.status === 'canceled')
    ) {
      setUpgrade(true);
    }
    history.replace({
      search: '',
    });
  }, [history, params, organisation]);

  useEffect(() => {
    let isSub = true;
    setBilling(undefined);
    organisationStore
      .getBilling(organisation)
      .then((billing) => isSub && setBilling(billing));
    return () => {
      isSub = false;
    };
  }, [organisation, organisationStore, billingState]);

  const subscriptionActive =
    billing &&
    billing.current_subscription &&
    ((billing.current_subscription.status === 'active' &&
      !billing.current_subscription.cancel_at_period_end) ||
      billing.current_subscription.status === 'trialing');
  const trialing =
    billing &&
    billing.current_subscription &&
    billing.current_subscription.trialing;
  const subscriptionCanceled =
    billing &&
    billing.current_subscription &&
    (billing.current_subscription.status === 'canceled' ||
      billing.current_subscription.cancel_at_period_end);

  return (
    <PageContainer>
      <UpgradeSideSheet
        isShown={upgrade}
        onClose={() => {
          setUpgrade(false);
          setBillingState((s) => s + 1);
        }}
      />
      <CreditCardDialog
        isShown={addNewCard}
        onClose={(changed) => {
          setAddNewCard(false);
          if (changed) {
            setBilling(undefined);
            setBillingState((s) => s + 1);
          }
        }}
      />
      <Container>
        <Button
          iconLeft="arrow-left"
          marginBottom={majorScale(2)}
          onClick={() =>
            history.length > 0 ? history.goBack() : history.push('/settings')
          }
        >
          <FormattedMessage defaultMessage="Settings" />
        </Button>

        <PageHeader
          title={intl.formatMessage({ defaultMessage: 'Subscription' })}
          actions={
            authStore.me!.id === authStore.currentOrganisation!.owner && (
              <Popover
                content={(close) => (
                  <Menu>
                    <MenuDivider
                      text={intl.formatMessage({ defaultMessage: 'Actions' })}
                    />
                    <MenuItem
                      icon="credit-card"
                      text={intl.formatMessage({
                        defaultMessage: 'Add credit card',
                      })}
                      onClick={() => {
                        close();
                        setAddNewCard(true);
                      }}
                    />
                    <MenuItem
                      text={intl.formatMessage({
                        defaultMessage: 'Update billing details',
                      })}
                      onClick={() => {
                        history.push('/settings/account');
                        close();
                      }}
                    />
                    <MenuDivider />
                    {subscriptionActive && !trialing && (
                      <MenuItem
                        text={intl.formatMessage({
                          defaultMessage: 'Cancel subscription',
                        })}
                        intent="danger"
                        onClick={() => {
                          close();
                          cancelAlert.show(async () => {
                            try {
                              await organisationStore.cancelSubscription(
                                organisation
                              );
                              setBillingState((s) => s + 1);
                              toaster.addToast({
                                intent: 'info',
                                message: intl.formatMessage({
                                  defaultMessage:
                                    'Subscription has been cancelled',
                                }),
                              });
                            } catch (err) {
                              console.error(err);
                              toaster.addToast({
                                intent: 'danger',
                                message: intl.formatMessage({
                                  defaultMessage:
                                    'An unexpected error occured. Please contact support to cancel your subscription.',
                                }),
                              });
                            }
                          });
                        }}
                      />
                    )}
                    {subscriptionCanceled && (
                      <MenuItem
                        text={intl.formatMessage({
                          defaultMessage: 'Reactivate subscription',
                        })}
                        icon="shopping-cart"
                        intent="info"
                        onClick={() => {
                          close();
                          setUpgrade(true);
                        }}
                      />
                    )}
                    {(trialing ||
                      !billing ||
                      !billing.current_subscription) && (
                      <MenuItem
                        text={intl.formatMessage({
                          defaultMessage: 'Upgrade subscription',
                        })}
                        icon="shopping-cart"
                        intent="info"
                        onClick={() => {
                          close();
                          setUpgrade(true);
                        }}
                      />
                    )}
                  </Menu>
                )}
              >
                <Button iconRight="chevron-down">
                  <FormattedMessage defaultMessage="Actions" />
                </Button>
              </Popover>
            )
          }
        />

        <Grid>
          <Section
            className="col-span-12 lg:col-span-4"
            title={intl.formatMessage({ defaultMessage: 'Overview' })}
          >
            {!billing ? (
              <Spinner size={16} />
            ) : (
              <>
                <Box className="p medium">
                  {intl.formatMessage({ defaultMessage: 'Plan details' })}
                </Box>
                {billing.current_subscription ? (
                  <>
                    <p className="mt-1 mb-4">
                      <FormattedMessage
                        defaultMessage="Sails – {users, plural, =1 {# user} other {# users}}"
                        values={{
                          users: billing.current_subscription.quantity,
                        }}
                      />
                      {billing.current_subscription.cancel_at_period_end && (
                        <Tag intent="danger" small className="ml-2">
                          <FormattedMessage
                            defaultMessage="Cancels on {cancelDate, date}"
                            values={{
                              cancelDate: new Date(
                                billing.current_subscription.current_period_end!
                              ),
                            }}
                          />
                        </Tag>
                      )}
                      {billing.current_subscription.status === 'canceled' && (
                        <Tag intent="danger" small className="ml-2">
                          <FormattedMessage defaultMessage="Canceled" />
                        </Tag>
                      )}
                      {billing.current_subscription.trialing && (
                        <Tag intent="info" small className="ml-2">
                          <FormattedMessage
                            defaultMessage="Trial ends {trialEndDate, date}"
                            description="The date for the trial end"
                            values={{
                              trialEndDate: new Date(
                                billing.current_subscription!.trial_end!
                              ),
                            }}
                          />
                        </Tag>
                      )}
                    </p>

                    {billing.current_subscription.status !== 'canceled' && (
                      <>
                        <Box className="p medium">
                          <FormattedMessage defaultMessage="Monthly amount (excl. tax)" />
                        </Box>
                        <Box
                          className="p font-mono"
                          marginTop={minorScale(1)}
                          marginBottom={majorScale(2)}
                        >
                          <FormattedNumber
                            format="currency"
                            currency={
                              billing.current_subscription.amount_currency
                            }
                            value={billing.current_subscription.amount}
                          />
                        </Box>
                      </>
                    )}

                    {subscriptionActive && (
                      <>
                        <Box className="p medium">
                          <FormattedMessage
                            defaultMessage="Next payment"
                            description="The date for the next payment"
                          />
                        </Box>
                        <Box
                          className="p"
                          marginTop={minorScale(1)}
                          marginBottom={majorScale(2)}
                        >
                          <FormattedDate
                            format="default"
                            value={
                              new Date(
                                billing.current_subscription!.current_period_end!
                              )
                            }
                          />
                        </Box>
                      </>
                    )}
                  </>
                ) : (
                  <p className="mt-1 mb-4 muted">
                    {' '}
                    <FormattedMessage defaultMessage="No current subscription" />{' '}
                  </p>
                )}

                <Box className="p medium">
                  <FormattedMessage defaultMessage="Payment method" />
                </Box>
                {billing.card && billing.card.last4 ? (
                  <>
                    <p className="p font-mono mt-1 mb-1">
                      {billing.card.brand.toUpperCase()} **** **** ****{' '}
                      {billing.card.last4}
                    </p>
                    <p className="small mb-4">
                      <FormattedMessage
                        defaultMessage="Expires {month}/{year}"
                        values={{
                          month: billing.card.exp_month,
                          year: billing.card.exp_year,
                        }}
                      />
                    </p>
                  </>
                ) : (
                  <p className="mt-1 mb-4 muted">
                    <FormattedMessage defaultMessage="No payment method attached" />
                  </p>
                )}
              </>
            )}
          </Section>
          <Section
            className="col-span-12 lg:col-span-8"
            title={intl.formatMessage({ defaultMessage: 'Invoice history' })}
          >
            {!billing ? (
              <Spinner size={16} />
            ) : billing.invoices.length === 0 ? (
              <p className="muted">
                <FormattedMessage defaultMessage="No invoices yet" />
              </p>
            ) : (
              <Table marginTop={majorScale(-2)}>
                <thead>
                  <tr>
                    <th>
                      <FormattedMessage
                        defaultMessage="Number"
                        description="Stripe invoice number"
                      />
                    </th>
                    <th>
                      <FormattedMessage
                        defaultMessage="Status"
                        description="Stripe invoice status"
                      />
                    </th>
                    <th>
                      <FormattedMessage
                        defaultMessage="Date"
                        description="Stripe invoice date"
                      />
                    </th>
                    <th className="right">
                      <FormattedMessage
                        defaultMessage="Total"
                        description="Stripe invoice total"
                      />
                    </th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {billing.invoices.map((invoice) => (
                    <tr key={invoice.id}>
                      <td>{invoice.number}</td>
                      <td>
                        <Tag
                          small
                          intent={
                            invoice.status === 'paid'
                              ? 'success'
                              : invoice.status === 'draft'
                              ? 'none'
                              : invoice.status === 'open'
                              ? 'info'
                              : 'danger'
                          }
                        >
                          {invoice.status === 'paid' ? (
                            <FormattedMessage defaultMessage="Paid" />
                          ) : invoice.status === 'draft' ? (
                            <FormattedMessage defaultMessage="Draft" />
                          ) : invoice.status === 'open' ? (
                            <FormattedMessage defaultMessage="Open" />
                          ) : (
                            <FormattedMessage defaultMessage="Void" />
                          )}
                        </Tag>
                      </td>
                      <td>
                        <FormattedDate
                          format="default"
                          value={new Date(invoice.created_at)}
                        />
                      </td>
                      <td className="right font-mono">
                        <FormattedNumber
                          format="currency"
                          currency={invoice.total_currency}
                          value={invoice.total}
                        />
                      </td>
                      <td>
                        <div className="flex items-center justify-end">
                          {invoice.pdf_url && (
                            <Button
                              display="inline-flex"
                              is="a"
                              href={invoice.pdf_url}
                              target="_blank"
                              minimal
                              iconRight="download"
                              marginLeft="auto"
                              marginRight={0}
                            >
                              <FormattedMessage defaultMessage="Download" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </Section>
        </Grid>
      </Container>
    </PageContainer>
  );
};
